import keys from '@debbie/common/dist/cache/keys.js'
import {
  Case,
  CaseStatus,
  EndReasonId,
  SystemEndReason,
} from '@debbie/common/dist/cases/index.js'
import { vouchersToMarkExpired } from '@debbie/common/dist/economy/helpers/expiration.js'
import { VoucherTypeIds } from '@debbie/common/dist/economy/vouchers/index.js'
import { UpdateType } from '@debbie/common/dist/updates/index.js'
import * as caseVouchersLib from '@debbie/cortex/dist/api/case-vouchers.js'
import * as casesAPI from '@debbie/cortex/dist/api/cases.js'
import * as updatesLib from '@debbie/cortex/dist/api/updates.js'
import { caseVouchersEntity } from '@debbie/cortex/dist/case-vouchers/index.js'
import {
  casesEntity,
  ensureCaseLoaded,
} from '@debbie/cortex/dist/cases/index.js'
import { h } from '@debbie/cortex/dist/index.js'
import q from '@debbie/cortex/dist/query.js'
import { RootState } from '@debbie/cortex/dist/reducer.js'
import { useTenant } from '@debbie/cortex/dist/tenant/index.js'
import { useUpdatesByRefId } from '@debbie/cortex/dist/updates/index.js'
import CostCeilingBanner from '@debbie/iris/dist/components/economy/case-vouchers/CostCeilingBanner.js'
import { DecoratedVoucher } from '@debbie/iris/dist/components/economy/case-vouchers/types.js'
import CreateVoucher from '@debbie/iris/dist/components/economy/CreateVoucher/index.js'
import { Button, ButtonDrop } from '@debbie/iris/dist/components/index.js'
import moment from 'moment'
import React, { useState } from 'react'
import { useSelector } from 'react-redux'

type Props = {
  singleCase: Case
  caseVouchers: DecoratedVoucher[]
}

const CaseEconomyHeader = (props: Props) => {
  const singleCase = props.singleCase

  const [addVoucherOpen, setAddVoucherOpen] = useState<boolean>(false)
  const [loadingExpirationMarking, setLoadingExpirationMarking] =
    useState<boolean>(false)

  const lang = useSelector((s: RootState) => s.lang)
  const tenant = useTenant()
  const updatesLoadable = useUpdatesByRefId(singleCase?.caseId)
  const caseUpdates = h.hasData(updatesLoadable) ? updatesLoadable.data : null

  const vouchersToBeMarkedExpired =
    tenant &&
    vouchersToMarkExpired(
      props.caseVouchers.filter(
        (v) => v.deletedAt == null && v.irrecoverableDate == null,
      ),
      tenant.timezone,
    )

  const markExpired = async () => {
    if (
      !vouchersToBeMarkedExpired ||
      !confirm(lang.economy.sureYouWantToMarkExpired)
    ) {
      return
    }
    setLoadingExpirationMarking(true)

    const toBeMarkedIds = vouchersToBeMarkedExpired.map((v) => v.caseVoucherId)

    try {
      await caseVouchersLib.markCaseVouchersExpired(toBeMarkedIds)
      q.invalidate(
        toBeMarkedIds.flatMap((caseVoucherId) =>
          keys.write.modify(caseVouchersEntity, caseVoucherId),
        ),
      )

      // After the marking, so engine 2 writes it off while the case is open.
      if (vouchersToBeMarkedExpired.length === props.caseVouchers.length) {
        await casesAPI.patch(singleCase.caseId, {
          status: CaseStatus.ENDED,
          endReasonId: EndReasonId[SystemEndReason.Expiration],
        })
        q.invalidate(keys.write.modify(casesEntity, singleCase.caseId))
        await ensureCaseLoaded(singleCase.caseId)
      } else if (caseUpdates) {
        await Promise.all(
          caseUpdates
            .filter(
              (update) =>
                (!update.continuedAt ||
                  moment().isBefore(moment(update.continuedAt))) &&
                update.refId &&
                toBeMarkedIds.includes(update.refId) &&
                (
                  [
                    UpdateType.DEBTEXPIRED,
                    UpdateType.DEBTEXPIRESWITHINTHREEMONTHS,
                  ] as string[]
                ).includes(update.updateTypeId),
            )
            .map((update) => updatesLib.patch(update.updateId)),
        )
      }
    } catch (err) {
      console.error(err)
      alert('Error')
    }

    setLoadingExpirationMarking(false)
  }

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5em' }}>
        {lang.economy.caseEconomy}
        <CostCeilingBanner singleCase={singleCase} />
      </div>
      <div>
        {singleCase &&
          props.caseVouchers &&
          vouchersToBeMarkedExpired &&
          !!vouchersToBeMarkedExpired.length && (
            <Button
              style={{
                marginRight: '5px',
              }}
              theme="secondary"
              size="mini"
              onClick={markExpired}
              loading={loadingExpirationMarking}
            >
              {vouchersToBeMarkedExpired.length === props.caseVouchers.length
                ? lang.economy.markExpired
                : `${lang.economy.markSomeExpired} ${vouchersToBeMarkedExpired.length} ${lang.economy.markSomeExpiredPostings}`}
            </Button>
          )}
        <ButtonDrop
          open={addVoucherOpen}
          onOpen={() => setAddVoucherOpen(true)}
          onClose={() => setAddVoucherOpen(false)}
          size="mini"
        >
          {singleCase && (
            <CreateVoucher
              caseId={singleCase.caseId}
              creditorId={singleCase.creditorId}
              onCreate={() => setAddVoucherOpen(false)}
              voucherTypeFilter={[VoucherTypeIds.PRINCIPAL]}
              caseVouchers={props.caseVouchers}
              singleCase={props.singleCase}
            />
          )}
        </ButtonDrop>
      </div>
    </div>
  )
}

export default CaseEconomyHeader
